import { EnumBetType, GameId } from '@bg-shared/enums';
import { API_URLS } from '@bg-shared/constants';
import { httpClientRepository } from '@bg-shared/infrastructure/HttpClient';
import {
    IFetchResultsParams,
    IResultsPage,
    IResultsPageItem,
} from '@bg-shared/business/Reports/interfaces_legacy/ResultsReport';
import { IGenericBet, ISingleBet } from '@bg-shared/business/Reports/interfaces_legacy/Bets';
import { reportsFormatter } from './Reports.formatter';
import { IBetHistoryParams, IBetHistoryResponse, IBetHistoryResponseMap } from './interfaces';

export default class ReportsApi {
    public fetchResults(partnerCode: string, params: IFetchResultsParams): Promise<IResultsPage> {
        return httpClientRepository.get<IResultsPage>(`${API_URLS.RESULTS_REPORT}/${partnerCode}`, {
            params,
        });
    }

    public fetchSingleResult(partnerCode: string, runCode: string): Promise<IResultsPageItem> {
        return httpClientRepository.get<IResultsPageItem>(
            `${API_URLS.SINGLE_RUN_RESULTS_REPORT}/${partnerCode}/${runCode}`,
        );
    }

    public async fetchBetHistory<T extends EnumBetType>(
        params: IBetHistoryParams,
    ): Promise<IBetHistoryResponse> {
        try {
            const formattedParams = reportsFormatter.formatParams(params);
            const response = await httpClientRepository.get<IBetHistoryResponseMap[T]>(
                API_URLS.BET_HISTORY_REPORT,
                {
                    params: formattedParams,
                },
            );

            return {
                nextOffsetId: response.nextOffsetId,
                singleCount: response.singleCount,
                combinationCount: response.combinationCount,
                subscriptionCount: response.subscriptionCount,
                days: reportsFormatter.formatBetsDays(response, params.timezone, params.dateFormat),
                type: params.type,
            };
        } catch (e) {
            console.error(e);

            return {
                nextOffsetId: null,
                singleCount: 0,
                combinationCount: 0,
                subscriptionCount: 0,
                days: [],
                type: params.type,
            };
        }
    }

    public async fetchRecentBets(limit: number): Promise<IGenericBet[]> {
        try {
            return await httpClientRepository.get<IGenericBet[]>(
                `${API_URLS.RECENT_BETS}/${limit}`,
                {
                    maxRetries: 3,
                },
            );
        } catch (error) {
            console.error('Recent bets API call error', error);
            return [];
        }
    }

    public async fetchRecentBetsPerRun(gameId: GameId, runId: number): Promise<IGenericBet[]> {
        try {
            return await httpClientRepository.get<IGenericBet[]>(
                `${API_URLS.RECENT_BETS_PER_RUN}/${gameId}/${runId}`,
            );
        } catch (error) {
            console.error('Recent bets API call error', error);
            return [];
        }
    }

    public fetchRepeatableBets(): Promise<ISingleBet[]> {
        return httpClientRepository.get<ISingleBet[]>(API_URLS.REPEATABLE_BETS);
    }

    public async getResults(
        partnerCode: string,
        params: IFetchResultsParams,
        runCode?: string,
    ): Promise<IResultsPage> {
        if (runCode) {
            return this.fetchSingleResult(partnerCode, runCode).then((data) => {
                if (!data) {
                    throw Error();
                }

                return {
                    pages: 0,
                    runs: [data],
                };
            });
        }

        return this.fetchResults(partnerCode, params);
    }
}

export const reportsApi = new ReportsApi();
